import React, { useEffect, useRef } from 'react';
import ReactDOM from 'react-dom';
import './PrivacyPolicyModal.css';

interface Props {
  onClose: () => void;
}

export const TermsOfUseModal: React.FC<Props> = ({ onClose }) => {
  const dialogRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const prev = document.activeElement as HTMLElement | null;
    dialogRef.current?.focus();
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('keydown', onKey);
      prev?.focus();
    };
  }, [onClose]);

  const modal = (
    <div className="ppm-backdrop" onClick={onClose} aria-hidden="true">
      <div
        className="ppm-card"
        ref={dialogRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby="tou-title"
        tabIndex={-1}
        onClick={e => e.stopPropagation()}
      >
        <div className="ppm-header">
          <h2 id="tou-title" className="ppm-title">Terms of Use</h2>
          <span className="ppm-updated">Updated 2026</span>
          <button className="ppm-close" onClick={onClose} aria-label="Close terms of use">
            <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
              <line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" />
            </svg>
          </button>
        </div>

        <div className="ppm-body">

          <section className="ppm-section">
            <h3 className="ppm-section-title">1. Accuracy of Information</h3>
            <p>You are solely responsible for the accuracy of any rent data, comments, or information you submit. By posting, you represent that the content is truthful to the best of your knowledge.</p>
            <ul className="ppm-list">
              <li><strong>Prohibited Content:</strong> You may not submit false listings, inaccurate pricing, defamatory remarks, or &ldquo;broker-driven&rdquo; listings disguised as genuine renter data.</li>
              <li><strong>Ontario Disclosure:</strong> Under the <em>Consumer Protection Act</em>, providing false or misleading representations is a violation of site policy and may result in immediate account termination.</li>
            </ul>
          </section>

          <section className="ppm-section">
            <h3 className="ppm-section-title">2. Matching and Contact Sharing (Privacy &amp; Consent)</h3>
            <p>By &ldquo;dropping a pin&rdquo; or creating a listing, you provide express consent for Gtarents.ca to share your contact information (email and phone number) with other users.</p>
            <ul className="ppm-list">
              <li><strong>How it Works:</strong> We only share your details when our system identifies a &ldquo;Match&rdquo; based on location, budget, and preferences (e.g., BHK, gender, smoking, food).</li>
              <li><strong>Delivery:</strong> Matches are delivered via a one-time automated email to both parties. Contact details are never displayed publicly on the map.</li>
              <li><strong>Withdrawal of Consent:</strong> You may withdraw your consent at any time by deleting your pin. However, we cannot retract contact information already shared in match emails sent while your pin was active.</li>
              <li><strong>Non-Discrimination &amp; Preference Matching:</strong> Gtarents.ca is committed to fostering an inclusive community. We provide filters for gender, smoking, and food preferences solely to facilitate compatible living arrangements as requested by our users. These filters are tools for user-to-user matching and are not used by the platform to discriminate against any individual or group. All users are entitled to equal access to the platform&rsquo;s services regardless of their background.</li>
            </ul>
          </section>

          <section className="ppm-section">
            <h3 className="ppm-section-title">3. Anti-Spam Compliance (CASL)</h3>
            <p>By using the matching service, you agree to receive automated &ldquo;Match Emails&rdquo; from gtarents.ca.</p>
            <ul className="ppm-list">
              <li>These emails are functional to the service you requested.</li>
              <li>The gtarents.ca team may be cc&rsquo;d on these emails for quality assurance and to prevent platform abuse.</li>
              <li>We will never sell your contact information to third parties or brokers for marketing purposes.</li>
            </ul>
          </section>

          <section className="ppm-section">
            <h3 className="ppm-section-title">4. Moderation &amp; Community Standards</h3>
            <ul className="ppm-list">
              <li><strong>Community Reporting:</strong> Any pin receiving 5 or more community reports will be automatically hidden pending review.</li>
              <li><strong>Right to Remove:</strong> We reserve the right to remove any content or ban any IP address at our sole discretion to maintain the integrity of the platform.</li>
            </ul>
          </section>

          <section className="ppm-section">
            <h3 className="ppm-section-title">5. No Warranty &amp; Limitation of Liability</h3>
            <ul className="ppm-list">
              <li><strong>User-Generated Content:</strong> All data is user-reported and unverified. Gtarents.ca does not guarantee the accuracy, safety, or legality of any listings. You are encouraged to verify all rental details independently.</li>
              <li><strong>&ldquo;As Is&rdquo; Service:</strong> To the maximum extent permitted by the laws of Ontario, this site is provided &ldquo;as is.&rdquo; Gtarents.ca and its operators shall not be liable for any direct, indirect, or consequential damages resulting from your use of the site or your interactions with other users.</li>
            </ul>
          </section>

          <section className="ppm-section">
            <h3 className="ppm-section-title">6. Intellectual Property</h3>
            <p>All site design, original code, and brand assets are the property of Gtarents.ca. Map data is provided via Google Maps and is subject to their respective terms.</p>
          </section>

          <section className="ppm-section">
            <h3 className="ppm-section-title">7. Takedown &amp; Disputes</h3>
            <p>If you believe any content violates your rights or Canadian law, contact us immediately at <a className="ppm-link" href="mailto:help@gtarents.ca">help@gtarents.ca</a>.</p>
          </section>

          <section className="ppm-section">
            <h3 className="ppm-section-title">8. Amendments</h3>
            <p>We may update these terms to reflect changes in the law or our services. If we make a material change (as defined by the <em>Consumer Protection Act, 2023</em>), we will provide 30 days&rsquo; notice via the email address associated with your account. Continued use after this period constitutes acceptance of the new terms.</p>
          </section>

        </div>

        <div className="ppm-footer">
          <button className="ppm-done-btn" onClick={onClose}>Close</button>
        </div>
      </div>
    </div>
  );

  return ReactDOM.createPortal(modal, document.body);
};
